import { ChannelSSOIntegration } from '@/types/channel';
import { ThemeName } from '@/types/theme';
import { isGreenroomMobileUrl } from '@/utils/validation/urls';
import qs from 'qs';
import { isObject } from 'underscore';

const getString = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const getMaybeString = (value: unknown): string | null => {
  return typeof value === 'string' ? value : null;
};

const getMaybeThemeName = (value: unknown): ThemeName | undefined => {
  return Object.values(ThemeName).includes(value as ThemeName) ? (value as ThemeName) : undefined;
};

export const isChannelSSOIntegration = (value: unknown): value is ChannelSSOIntegration => {
  return isObject(value) && 'idp_name' in value && 'integration_provider' in value;
};

interface iPageRequest {
  redirect_uri: string;
  state: string | null;
  resource?: string | null;
  email?: string | null;
  channel?: string | null;
  from_app?: string | null;
  integration?: ChannelSSOIntegration;
  redirect_to?: string | null;
  no_magic_link?: string | null;
  theme?: ThemeName | null;
}

interface PageRequestJson extends iPageRequest {
  i?: ChannelSSOIntegration;
  integration?: never;
}

export class PageRequest implements iPageRequest {
  constructor(
    public redirect_uri: string,
    public state: string | null,
    public resource: string | null,
    public email: string | null,
    public channel: string | null,
    public from_app: string | null,
    public integration?: ChannelSSOIntegration,
    public redirect_to?: string | null,
    public no_magic_link?: string | null,
    public theme?: ThemeName
  ) {}

  public static fromSearchParams(params: URLSearchParams): PageRequest {
    const parsed = qs.parse(params.toString());
    const redirectUri = getString(parsed.redirect_uri);
    const state = getMaybeString(parsed.state);
    const resource = getMaybeString(parsed.resource);
    const email = getMaybeString(parsed.email);
    const channel = getMaybeString(parsed.channel);
    const from_app = getMaybeString(parsed.from_app) || null;
    const integration = isChannelSSOIntegration(parsed.i) ? parsed.i : undefined;
    const redirectTo = getMaybeString(parsed.redirect_to);
    const noMagicLink = getMaybeString(parsed.no_magic_link);
    const theme = getMaybeThemeName(parsed.theme);

    return new PageRequest(
      redirectUri,
      state,
      resource,
      email,
      channel,
      from_app,
      integration,
      redirectTo,
      noMagicLink,
      theme
    );
  }

  public static fromJson(params: PageRequestJson): PageRequest {
    return new PageRequest(
      params.redirect_uri,
      params.state,
      params.resource || null,
      params.email || null,
      params.channel || null,
      params.from_app || 'Brandlive Platform',
      params.i,
      params.redirect_to,
      params.no_magic_link || null,
      params.theme || undefined
    );
  }

  isIOS = () => {
    return isGreenroomMobileUrl(this.redirect_uri);
  };

  toJson = (): PageRequestJson => {
    const json: PageRequestJson = {
      redirect_uri: this.redirect_uri,
      state: this.state,
    };

    if (this.resource) {
      json.resource = this.resource;
    }

    if (this.email) {
      json.email = this.email;
    }

    if (this.channel) {
      json.channel = this.channel;
    }

    if (this.integration) {
      json.i = this.integration;
    }

    if (this.from_app) {
      json.from_app = this.from_app;
    }

    if (this.redirect_to) {
      json.redirect_to = this.redirect_to;
    }

    if (this.theme) {
      json.theme = this.theme;
    }

    return json;
  };

  toParams = (): string => {
    return qs.stringify(this.toJson(), { skipNulls: true });
  };
}
